// elevation.js — Höhenprofil einer Route: Fläche über km, Kante nach Steigung eingefärbt, Tunnel/Brücken als Bänder,
// Abtasten per Zeiger und Pfeiltasten (role=slider) mit Rückruf für die Karte; versteckte Datentabelle wie bars.js.
import { html, unsafeHtml, attrs } from '../core/h.js';

/** Farbklasse einer Steigung (Betrag): bis 6 % neutral, bis 10 % Warnung, darüber Gefahr. */
export function gradeClass(g) {
  const a = Math.abs(Number(g) || 0);
  return a <= 6 - 1e-9 ? 'flat' : a <= 10 ? 'warn' : 'steep';
}

/** Geometrie in einem w×h-Feld. Grundlinie = tiefster Punkt (nicht 0), sonst wäre jede Alpenroute ein flacher Strich. */
export function elevationLayout(samples, grades, { w = 600, h = 140 } = {}) {
  const s = (samples || []).filter((p) => p && Number.isFinite(p.ele));
  if (s.length < 2) return { area: '', runs: [], bands: [], min: null, max: null, x: () => 0, y: () => 0, total: 0 };
  const total = s.at(-1).d || 1;
  let min = Math.min(...s.map((p) => p.ele)), max = Math.max(...s.map((p) => p.ele));
  if (max - min < 10) { const m = (max + min) / 2; min = m - 5; max = m + 5; }
  const f = (n) => Math.round(n * 10) / 10;
  const x = (d) => f((d / total) * w), y = (e) => f(h - ((e - min) / (max - min)) * h);
  const area = `M0,${h} ` + s.map((p) => `L${x(p.d)},${y(p.ele)}`).join(' ') + ` L${w},${h} Z`;
  const runs = [];
  for (let i = 0; i < s.length - 1; i++) {
    const g = grades && grades[i] ? grades[i].grade : 0, cls = gradeClass(g), last = runs.at(-1);
    const seg = `L${x(s[i + 1].d)},${y(s[i + 1].ele)}`;
    if (last && last.cls === cls) last.d += ' ' + seg;
    else runs.push({ cls, d: `M${x(s[i].d)},${y(s[i].ele)} ${seg}` });
  }
  const bands = [];
  for (let i = 0; i < s.length; i++) {
    if (!s[i].st) continue;
    const last = bands.at(-1), from = i > 0 ? s[i - 1].d : s[i].d, to = i < s.length - 1 ? s[i + 1].d : s[i].d;
    if (last && last.kind === s[i].st && last.toI === i - 1) { last.to = to; last.toI = i; } else bands.push({ kind: s[i].st, from, to, toI: i });
  }
  return { area, runs, bands: bands.map((b) => ({ kind: b.kind, x: x(b.from), w: f(x(b.to) - x(b.from)) })), min, max, x, y, total };
}

/** Messpunkt, der der Strecke d (m) am nächsten liegt, mit der Steigung seines Abschnitts. */
export function pointAt(samples, grades, d) {
  let best = 0;
  for (let i = 1; i < samples.length; i++) if (Math.abs(samples[i].d - d) < Math.abs(samples[best].d - d)) best = i;
  const p = samples[best], g = grades[Math.min(best, grades.length - 1)];
  return { d: p.d, ele: p.ele, lat: p.lat, lng: p.lng, grade: g ? g.grade : 0, i: best };
}

/** Steile Abschnitte für die Schwelle des Fahrzeugs (Server liefert je Schwelle eine Liste). */
export function steepFor(steep, limit) {
  return limit && steep && Array.isArray(steep[limit.limit]) ? steep[limit.limit] : [];
}

/**
 * Diagramm. text: { aria, legend: {flat, warn, steep, tunnel, bridge}, km(d) → '12,4 km', m(e) → '1.230 m', pct(g) → '7 %',
 * readout(p) → Text des Lesepunkts, cols: [Strecke, Höhe, Steigung] }.
 */
export function elevationChart({ samples, grades, text }) {
  const W = 600, H = 140;
  const L = elevationLayout(samples, grades, { w: W, h: H });
  if (!L.runs.length) return '';
  const bands = L.bands.map((b) => `<rect class="vc-elev-band is-${b.kind}" x="${b.x}" y="0" width="${Math.max(b.w, 1)}" height="${H}"/>`).join('');
  const runs = L.runs.map((r) => `<path class="vc-elev-run is-${r.cls}" d="${r.d}"/>`).join('');
  const kinds = new Set(L.bands.map((b) => b.kind));
  const rows = samples.filter((_, i) => i % Math.max(1, Math.ceil(samples.length / 24)) === 0 || i === samples.length - 1);
  const p0 = pointAt(samples, grades, 0);
  return html`<figure class="vc-elev" data-elev>
    <div class="vc-elev-plot">
      <span class="vc-elev-y is-max t-label-s t-muted" aria-hidden="true">${text.m(L.max)}</span>
      <span class="vc-elev-y is-min t-label-s t-muted" aria-hidden="true">${text.m(L.min)}</span>
      <div class="vc-elev-box">
        ${unsafeHtml(`<svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" aria-hidden="true" focusable="false">${bands}<path class="vc-elev-area" d="${L.area}"/>${runs}</svg>`)}
        <span class="vc-elev-cursor" data-elev-cursor hidden></span>
        <span class="vc-elev-tip t-label-m" data-elev-tip hidden></span>
        <div class="vc-elev-scrub" data-elev-scrub tabindex="0" role="slider"
          ${attrs({ 'aria-label': text.aria, 'aria-valuemin': 0, 'aria-valuemax': Math.round(L.total), 'aria-valuenow': 0, 'aria-valuetext': text.readout(p0) })}></div>
      </div>
    </div>
    <div class="vc-elev-x t-label-s t-muted" aria-hidden="true"><span>${text.km(0)}</span><span>${text.km(L.total / 2)}</span><span>${text.km(L.total)}</span></div>
    <figcaption class="vc-bars-legend t-label-m t-muted">
      <span><i class="vc-elev-key is-flat"></i>${text.legend.flat}</span><span><i class="vc-elev-key is-warn"></i>${text.legend.warn}</span><span><i class="vc-elev-key is-steep"></i>${text.legend.steep}</span>
      ${[...kinds].map((k) => html`<span><i class="vc-elev-key is-band is-${k}"></i>${text.legend[k]}</span>`)}
    </figcaption>
    <div class="vc-visually-hidden"><table><caption>${text.aria}</caption>
      <thead><tr>${text.cols.map((c) => html`<th scope="col">${c}</th>`)}</tr></thead>
      <tbody>${rows.map((s) => { const p = pointAt(samples, grades, s.d); return html`<tr><th scope="row">${text.km(s.d)}</th><td>${text.m(s.ele)}</td><td>${text.pct(p.grade)}</td></tr>`; })}</tbody>
    </table></div>
  </figure>`;
}

/**
 * Abtasten: Zeiger (waagerecht ziehen, senkrecht scrollt die Seite weiter) und Tastatur (Pfeile 1 %, Bild ↑/↓ 10 %,
 * Pos1/Ende). onScrub(punkt|null) bewegt die Markierung auf der Karte. → Aufräumfunktion.
 */
export function bindElevation(root, { samples, grades, text, onScrub }) {
  const fig = root.querySelector('[data-elev]');
  if (!fig) return () => {};
  const scrub = fig.querySelector('[data-elev-scrub]'), cur = fig.querySelector('[data-elev-cursor]'), tip = fig.querySelector('[data-elev-tip]');
  const total = samples.at(-1).d || 1;
  let at = 0;
  const show = (d) => {
    at = Math.max(0, Math.min(total, d));
    const p = pointAt(samples, grades, at), pct = (p.d / total) * 100;
    cur.hidden = false; tip.hidden = false;
    cur.style.left = pct + '%';
    tip.style.left = pct + '%'; tip.dataset.side = pct > 60 ? 'left' : 'right';
    tip.textContent = text.readout(p);
    scrub.setAttribute('aria-valuenow', String(Math.round(p.d)));
    scrub.setAttribute('aria-valuetext', text.readout(p));
    if (onScrub) onScrub(p);
  };
  const hide = () => { cur.hidden = true; tip.hidden = true; if (onScrub) onScrub(null); };
  const fromEvent = (e) => { const r = scrub.getBoundingClientRect(); return ((e.clientX - r.left) / r.width) * total; };
  const handlers = {
    pointermove: (e) => show(fromEvent(e)),
    pointerdown: (e) => show(fromEvent(e)),
    pointerleave: (e) => { if (e.pointerType === 'mouse') hide(); },
    blur: hide,
    focus: () => show(at),
    keydown: (e) => {
      const step = { ArrowRight: 0.01, ArrowUp: 0.01, ArrowLeft: -0.01, ArrowDown: -0.01, PageUp: 0.1, PageDown: -0.1 }[e.key];
      if (step != null) { e.preventDefault(); show(at + step * total); }
      else if (e.key === 'Home') { e.preventDefault(); show(0); }
      else if (e.key === 'End') { e.preventDefault(); show(total); }
    },
  };
  for (const [k, f] of Object.entries(handlers)) scrub.addEventListener(k, f);
  return () => { for (const [k, f] of Object.entries(handlers)) scrub.removeEventListener(k, f); };
}
